import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { defaultPerfConfig, MEMBER_LOAD_LIMIT, perfConfigSchema, withRampRates, type LoadShape } from '@1qa/shared';
import { CheckCircle2, CircleAlert, Gauge, Play, ShieldCheck } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useRunCollection } from '@/lib/runCollection';
import { describeLoad, describeModel, formatNumber, scenarioPeaks } from '@/lib/perf';
import type { Project, TestCase } from '@/lib/types';
import { RunCollectionPicker } from '@/components/testdata/RunCollectionPicker';
import { Badge, Button, Dialog, Field, Input, Spinner, useToast } from '@/components/ui';
import { RampRateField } from './LoadPlanEditor';

const LIMIT_WORDS = { memory: 'memory', cpu: 'CPU', network: 'network', sockets: 'connections' } as const;

/** Debounced value: the capacity check runs when the person stops typing. */
function useDebounced<T>(value: T, ms = 500): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Start a performance test: a 1-user check of the script, or the load. Shows the agents that will run it, how many users
 * they can carry, and whether the person may start this load (load limits).
 */
export function PerfRunDialog({ project, test, open, onClose }: { project: Project; test: TestCase; open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { currentOrgRole } = useAuth();
  const runData = useRunCollection(project.id);
  const plan = perfConfigSchema.safeParse(test.perfConfig).data ?? defaultPerfConfig();
  // This run's load: the test's shape, with users / times changed here (own stages run as saved).
  const [load, setLoad] = useState<LoadShape>(plan.load);
  const changed = JSON.stringify(load) !== JSON.stringify(plan.load);
  const valid = load.target >= 1 && (load.stages ? true : load.rampUpS + load.steadyS + load.rampDownS > 0);
  const debounced = useDebounced(load);
  const capacity = useQuery({
    queryKey: ['perf-capacity', test.id, JSON.stringify(debounced)],
    queryFn: () => api.perfCapacity(test.id, debounced),
    enabled: open && valid,
    refetchInterval: 15_000,
  });
  const model = capacity.data?.model;
  const start = useMutation({
    mutationFn: (verify: boolean) => api.triggerPerfRun(project.id, test.id, verify ? { verify: true } : changed ? { load } : {}, runData.collectionId),
    onSuccess: (run, verify) => {
      toast.success(verify ? 'Check run started' : 'Load test started');
      navigate(`/runs/${run.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const decision = capacity.data?.decision;
  const freeAgents = useMemo(() => capacity.data?.agents.filter((a) => a.available) ?? [], [capacity.data]);
  const minutes = (k: 'rampUpS' | 'steadyS' | 'rampDownS') => ({
    value: Math.round((load[k] / 60) * 100) / 100,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setLoad((l) => ({ ...l, [k]: Math.max(0, Math.round(Number(e.target.value) * 60)) })),
  });
  // Users: ramps as users per second / minute (the times follow from the total, as on the test's page).
  const change = (patch: Partial<LoadShape>) => setLoad((l) => withRampRates({ ...l, ...patch }));
  // Each scenario's users at the peak: the test's split, scaled to this run's maximum.
  const peaks = scenarioPeaks({ load, scenarios: plan.scenarios });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={`Run “${test.name}”`}
      description="First check every scenario with one user; then put the app under load."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => start.mutate(true)} loading={start.isPending && start.variables === true} disabled={!freeAgents.length} icon={<ShieldCheck className="size-4" />}>
            Check the scripts (1 user)
          </Button>
          <Button onClick={() => start.mutate(false)} loading={start.isPending && start.variables === false} disabled={!valid || !decision?.ok || !freeAgents.length} icon={<Play className="size-4" />}>
            Start load
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="rounded-card bg-surface-sunken p-4 text-sm ring-1 ring-border-subtle ring-inset">
          <div className="font-medium text-fg">
            {plan.scenarios.length === 1
              ? plan.scenarios[0]!.name
              : `${plan.scenarios.length} scenarios: ${plan.scenarios.map((sc, i) => (load.mode === 'users' ? `${sc.name} ${formatNumber(peaks[i] ?? 0)} users` : `${sc.name} ${formatNumber(sc.share)} %`)).join(', ')}`}
          </div>
          <p className="mt-1 text-xs text-fg-muted">
            {describeLoad(load)}
            {changed ? ' (this run only)' : ''}
          </p>
        </div>
        {load.stages ? (
          <p className="text-xs text-fg-muted">This test has its own stages; change them on the test’s page.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-4">
            <Field label={load.mode === 'users' ? 'Maximum users' : `New users / ${load.timeUnit === 'm' ? 'min' : 's'}`} hint={load.mode === 'users' && currentOrgRole === 'MEMBER' ? `Up to ${MEMBER_LOAD_LIMIT.vus} for Members` : 'At the peak'}>
              {(id) => <Input id={id} type="number" min={1} max={1_000_000} value={load.target} onChange={(e) => change({ target: Math.max(1, Math.round(Number(e.target.value))) })} />}
            </Field>
            {load.mode === 'users' ? (
              <RampRateField label="Ramp-up" verb="start" total={load.target} seconds={load.rampUpS} rate={load.rampUpRate} onChange={(r) => change({ rampUpRate: r.rate, rampUpS: r.seconds })} />
            ) : (
              <Field label="Ramp-up (min)">{(id) => <Input id={id} type="number" min={0} step={0.5} {...minutes('rampUpS')} />}</Field>
            )}
            <Field label="Steady (min)">{(id) => <Input id={id} type="number" min={0} step={0.5} {...minutes('steadyS')} />}</Field>
            {load.mode === 'users' ? (
              <RampRateField label="Ramp-down" verb="stop" total={load.target} seconds={load.rampDownS} rate={load.rampDownRate} onChange={(r) => change({ rampDownRate: r.rate, rampDownS: r.seconds })} />
            ) : (
              <Field label="Ramp-down (min)">{(id) => <Input id={id} type="number" min={0} step={0.5} {...minutes('rampDownS')} />}</Field>
            )}
          </div>
        )}
        {runData.collections.length > 0 && <RunCollectionPicker state={runData} />}

        <section aria-labelledby="perf-agents" className="space-y-2">
          <h3 id="perf-agents" className="text-xs font-semibold tracking-label text-fg-muted uppercase">
            Load agents
          </h3>
          {capacity.isPending ? (
            <Spinner />
          ) : capacity.isError ? (
            <p className="text-sm text-danger-text">{errorMessage(capacity.error)}</p>
          ) : capacity.data.agents.length === 0 ? (
            <p className="text-sm text-fg-secondary">
              No load agent can run this project’s tests yet. Connect one on{' '}
              <Link to="/helpers/load-agents" className="text-link hover:underline">
                Helper services › Load agents
              </Link>
              .
            </p>
          ) : (
            <ul className="divide-y divide-border-subtle rounded-card ring-1 ring-border-subtle ring-inset">
              {capacity.data.agents.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm">
                  <Gauge className="size-4 text-fg-muted" aria-hidden />
                  <span className="font-medium text-fg">{a.name}</span>
                  {a.available ? <Badge tone="success">Free</Badge> : <Badge tone="muted">{a.unavailable}</Badge>}
                  {a.estimate && (
                    <span className="ml-auto text-xs text-fg-secondary">
                      about {formatNumber(a.estimate.vus)} users (limited by {LIMIT_WORDS[a.estimate.limitedBy]}){a.estimate.estimated ? ', estimate' : ''}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {capacity.data && capacity.data.agents.length > 0 && !freeAgents.length ? (
          <p role="status" className="flex items-start gap-2 rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset">
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" />
            <span>No load agent is free right now (busy with another run, paused or offline). Wait until a run ends, or start another agent.</span>
          </p>
        ) : decision && (
          <p
            role="status"
            className={
              decision.ok
                ? 'flex items-start gap-2 rounded-field bg-success-subtle p-3 text-sm text-fg ring-1 ring-success/40 ring-inset'
                : 'flex items-start gap-2 rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset'
            }
          >
            {decision.ok ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" /> : <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" />}
            <span>
              {decision.ok
                ? decision.aboveEstimate
                  ? `${model ? describeModel(model) : 'This load'}: more than the free agents are estimated to carry (about ${formatNumber(capacity.data!.estimateVus)} users). You can start it as a Project Admin; the report will say so.`
                  : `${model ? describeModel(model) : 'This load'}. The free agents can carry about ${formatNumber(capacity.data!.estimateVus)} users of this test.`
                : decision.message}
            </span>
          </p>
        )}
      </div>
    </Dialog>
  );
}
